import {
  Button,
  Checkbox,
  Field,
  Form,
  H3,
  NumberInput,
  Select,
  Switch,
  SwitchSizeVariant,
  Text,
} from '@payaca/ambrosia';
import {
  DEFAULT_SOLAR_PLACEMENT_RULES,
  SUN_ACCESS_PRESETS,
  sunAccessPresetFromPercent,
  type SolarPlacementRules,
} from '@payaca/helpers/solarPlacementRules';
import { FC, ReactNode, useState, type JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';

export type EditablePlacementRules = Omit<SolarPlacementRules, 'panelGapM'>;

type Props<T extends EditablePlacementRules> = {
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  /** UK uses millimetres; US uses inches. Stored values stay metres. */
  region?: 'uk' | 'us';
  /** Hide rectangles/flat-roof if the packer cannot honour them yet. */
  advancedEnabled?: boolean;
  /**
   * `designer`: the in-design Array rules dialog. No helper text, one
   * "Exclude north-facing roofs" switch instead of the bearing window, and no
   * row-spacing multiplier (the packer computes tilted-row spacing from tilt
   * and site latitude; the account value is only a floor). Company settings
   * keep the full numeric fields.
   */
  variant?: 'settings' | 'designer';
};

const MM_PER_M = 1000;
const IN_PER_M = 1 / 0.0254;

const lengthLabel = (region: 'uk' | 'us') => (region === 'us' ? 'in' : 'mm');

const fromMetres = (metres: number, region: 'uk' | 'us'): number =>
  region === 'us'
    ? Math.round(metres * IN_PER_M * 10) / 10
    : Math.round(metres * MM_PER_M);

const toMetres = (display: number, region: 'uk' | 'us'): number =>
  region === 'us' ? display / IN_PER_M : display / MM_PER_M;

/**
 * In the designer each value is a fact row ("Edge setback  400 mm") with a
 * small Override that reveals the field; company settings show the field.
 */
const RuleFact: FC<{
  designer: boolean;
  label: string;
  value: string;
  children: ReactNode;
}> = ({ designer, label, value, children }) => {
  const [open, setOpen] = useState(false);
  if (!designer || open) return <>{children}</>;
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <Text as="span">{label}</Text>
      <div className="flex items-center gap-1">
        <Text as="span">{value}</Text>
        <Button
          variant="quaternary"
          size="sm"
          aria-label={`Override ${label.toLowerCase()}`}
          onClick={() => setOpen(true)}
        >
          Override
        </Button>
      </div>
    </div>
  );
};

const PlacementRulesFields = <T extends EditablePlacementRules>({
  value,
  onChange,
  disabled,
  region = 'uk',
  advancedEnabled = true,
  variant = 'settings',
}: Props<T>): JSX.Element => {
  const designer = variant === 'designer';
  const patch = (partial: Partial<EditablePlacementRules>) =>
    onChange({ ...value, ...partial });
  const sunPreset = sunAccessPresetFromPercent(value.minSunAccessPercent);
  const bearingWindow = value.roofBearingWindowDeg;
  // The fields are controlled by the caller; the Form gives them valid,
  // accessible markup. Nothing submits.
  const methods = useForm();

  return (
    <Form
      methods={methods}
      onSubmit={() => undefined}
      className="flex flex-col gap-4"
    >
      <div className="space-y-2">
        <H3>Edges</H3>
        <RuleFact
          designer={designer}
          label="Edge setback"
          value={`${fromMetres(value.edgeSetbackM, region)} ${lengthLabel(region)}`}
        >
          <Field.Legacy>
            <Field.Label>Edge setback ({lengthLabel(region)})</Field.Label>
            <NumberInput
              value={fromMetres(value.edgeSetbackM, region)}
              min={0}
              max={region === 'us' ? 40 : 1000}
              step={region === 'us' ? 0.5 : 10}
              disabled={disabled}
              onChange={(display) =>
                patch({
                  edgeSetbackM:
                    display == null
                      ? value.edgeSetbackM
                      : toMetres(display, region),
                })
              }
            />
            {!designer && (
              <Field.HelperText>
                {region === 'us'
                  ? 'Distance from the rake, ridge and eaves. Typical fire-break is 12–18 in.'
                  : 'Distance from the verge, ridge and eaves. MCS typically 400 mm.'}
              </Field.HelperText>
            )}
          </Field.Legacy>
        </RuleFact>
        {(
          [
            ['columnGapM', 'Column gap', 'Between panels across each row.'],
            ['rowGapM', 'Row gap', 'Between successive rows of panels.'],
          ] as const
        ).map(([key, label, description]) => (
          <RuleFact
            key={key}
            designer={designer}
            label={label}
            value={`${fromMetres(value[key], region)} ${lengthLabel(region)}`}
          >
            <Field.Legacy>
              <Field.Label>
                {label} ({lengthLabel(region)})
              </Field.Label>
              <NumberInput
                value={fromMetres(value[key], region)}
                min={0}
                max={region === 'us' ? 20 : 500}
                step={region === 'us' ? 0.1 : 1}
                disabled={disabled}
                onChange={(display) => {
                  if (display != null)
                    patch({ [key]: toMetres(display, region) });
                }}
              />
              {!designer && (
                <Field.HelperText>
                  {description} Rotating a module to landscape keeps these
                  directions.
                </Field.HelperText>
              )}
            </Field.Legacy>
          </RuleFact>
        ))}
      </div>
      <div className="space-y-2">
        <H3>Which roofs get panels</H3>
        {designer ? (
          <div className="flex items-center justify-between gap-3">
            <Text as="span">Exclude north-facing roofs</Text>
            <Switch
              aria-label="Exclude north-facing roofs"
              sizeVariant={SwitchSizeVariant.SM}
              value={value.excludeNorthFacing}
              disabled={disabled}
              onChange={(excludeNorthFacing) => patch({ excludeNorthFacing })}
            />
          </div>
        ) : (
          <>
            <Checkbox
              label="Restrict roof bearings"
              checked={value.excludeNorthFacing}
              disabled={disabled}
              onChange={(event) =>
                patch({ excludeNorthFacing: event.target.checked })
              }
            />
            <Text level="supporting">
              Only packs roofs in the inclusive clockwise window. Bearings are
              degrees clockwise from north: east 90°, south 180°, west 270°.
              360° equals 0°. A start above the end crosses north; equal
              endpoints select one bearing. Turn off to allow every bearing.
            </Text>
            {(['min', 'max'] as const).map((endpoint) => (
              <Field.Legacy key={endpoint}>
                <Field.Label>
                  {endpoint === 'min'
                    ? 'Start roof bearing (°)'
                    : 'End roof bearing (°)'}
                </Field.Label>
                <Controller
                  name={`roofBearingWindowDeg.${endpoint}`}
                  control={methods.control}
                  render={({ field }) => (
                    <NumberInput
                      {...field}
                      value={bearingWindow[endpoint]}
                      min={0}
                      max={360}
                      step={1}
                      disabled={disabled || !value.excludeNorthFacing}
                      onChange={(degrees) => {
                        if (
                          degrees == null ||
                          !Number.isFinite(degrees) ||
                          degrees < 0 ||
                          degrees > 360
                        )
                          return;
                        patch({
                          roofBearingWindowDeg: {
                            ...bearingWindow,
                            [endpoint]: degrees,
                          },
                        });
                        field.onChange(degrees);
                      }}
                    />
                  )}
                />
              </Field.Legacy>
            ))}
          </>
        )}
        <RuleFact
          designer={designer}
          label="Minimum panels per roof"
          value={String(value.minPanelsPerRoof)}
        >
          <Field.Legacy>
            <Field.Label>Minimum panels per roof</Field.Label>
            <NumberInput
              value={value.minPanelsPerRoof}
              min={0}
              max={50}
              step={1}
              disabled={disabled}
              onChange={(minPanelsPerRoof) =>
                patch({ minPanelsPerRoof: minPanelsPerRoof ?? 0 })
              }
            />
          </Field.Legacy>
        </RuleFact>
        <RuleFact
          designer={designer}
          label="Minimum roof slope"
          value={`${value.minSlopeDeg}°`}
        >
          <Field.Legacy>
            <Field.Label>Minimum roof slope (°)</Field.Label>
            <NumberInput
              value={value.minSlopeDeg}
              min={0}
              max={80}
              step={1}
              disabled={disabled}
              onChange={(minSlopeDeg) =>
                patch({ minSlopeDeg: minSlopeDeg ?? 0 })
              }
            />
          </Field.Legacy>
        </RuleFact>
        <RuleFact
          designer={designer}
          label="Skip poor sun"
          value={
            { off: 'Off', light: 'Below 70%', strict: 'Below 85%' }[sunPreset]
          }
        >
          <Field.Legacy>
            <Field.Label>Skip poor sun</Field.Label>
            <Select
              multiple={false}
              value={sunPreset}
              disabled={disabled}
              onChange={(preset) =>
                patch({
                  minSunAccessPercent: SUN_ACCESS_PRESETS[preset ?? 'off'],
                })
              }
              options={[
                { value: 'off', label: 'Off' },
                { value: 'light', label: 'Light (below 70%)' },
                { value: 'strict', label: 'Strict (below 85%)' },
              ]}
            />
          </Field.Legacy>
        </RuleFact>
      </div>
      <div className="space-y-2">
        <H3>How arrays look</H3>
        <Checkbox
          label="Keep each roof as one block"
          checked={value.singleIsland}
          disabled={disabled}
          onChange={(event) => patch({ singleIsland: event.target.checked })}
        />
        <Checkbox
          label="One orientation per roof"
          checked={value.singleOrientation}
          disabled={disabled}
          onChange={(event) =>
            patch({ singleOrientation: event.target.checked })
          }
        />
        <Checkbox
          label="Complete rectangles only"
          checked={value.quadrilateralArrays}
          disabled={disabled || !advancedEnabled}
          onChange={(event) =>
            patch({ quadrilateralArrays: event.target.checked })
          }
        />
        {!designer && (
          <Text level="supporting">
            One orientation, one filled block you can rail. Uses fewer panels on
            an irregular roof.
          </Text>
        )}
      </div>
      <div className="space-y-2">
        <H3>Flat roofs</H3>
        <RuleFact
          designer={designer}
          label="Flat at or under"
          value={`${value.flatRoof.maxSlopeDeg}°`}
        >
          <Field.Legacy>
            <Field.Label>Flat at or under (°)</Field.Label>
            <NumberInput
              value={value.flatRoof.maxSlopeDeg}
              min={0}
              max={45}
              step={1}
              disabled={disabled || !advancedEnabled}
              onChange={(maxSlopeDeg) =>
                patch({
                  flatRoof: {
                    ...value.flatRoof,
                    maxSlopeDeg:
                      maxSlopeDeg ??
                      DEFAULT_SOLAR_PLACEMENT_RULES.flatRoof.maxSlopeDeg,
                  },
                })
              }
            />
          </Field.Legacy>
        </RuleFact>
        <RuleFact
          designer={designer}
          label="Frame tilt"
          value={`${value.flatRoof.frameTiltDeg}°`}
        >
          <Field.Legacy>
            <Field.Label>Frame tilt (°)</Field.Label>
            <NumberInput
              value={value.flatRoof.frameTiltDeg}
              min={0}
              max={45}
              step={1}
              disabled={disabled || !advancedEnabled}
              onChange={(frameTiltDeg) =>
                patch({
                  flatRoof: {
                    ...value.flatRoof,
                    frameTiltDeg:
                      frameTiltDeg ??
                      DEFAULT_SOLAR_PLACEMENT_RULES.flatRoof.frameTiltDeg,
                  },
                })
              }
            />
          </Field.Legacy>
        </RuleFact>
        <RuleFact
          designer={designer}
          label="Tilted-row clearance"
          value={
            value.flatRoof.rowSpacingMode === 'fixed'
              ? 'Fixed clear gap'
              : 'Calculated from panel rise'
          }
        >
          <Field.Legacy>
            <Field.Label>Tilted-row clearance</Field.Label>
            <Select
              multiple={false}
              value={value.flatRoof.rowSpacingMode}
              disabled={disabled || !advancedEnabled}
              options={[
                { value: 'calculated', label: 'Calculated from panel rise' },
                { value: 'fixed', label: 'Fixed clear gap' },
              ]}
              onChange={(mode) =>
                patch({
                  flatRoof: {
                    ...value.flatRoof,
                    rowSpacingMode: mode ?? 'calculated',
                    ...(mode === 'fixed'
                      ? { fixedClearGapM: value.flatRoof.fixedClearGapM ?? 0 }
                      : {}),
                  },
                })
              }
            />
          </Field.Legacy>
        </RuleFact>
        {value.flatRoof.rowSpacingMode === 'fixed' ? (
          <Field.Legacy>
            <Field.Label>
              Fixed clear row gap ({lengthLabel(region)})
            </Field.Label>
            <NumberInput
              value={fromMetres(value.flatRoof.fixedClearGapM ?? 0, region)}
              min={0}
              max={region === 'us' ? 390 : 10000}
              step={region === 'us' ? 0.1 : 1}
              disabled={disabled || !advancedEnabled}
              onChange={(display) => {
                if (display != null)
                  patch({
                    flatRoof: {
                      ...value.flatRoof,
                      fixedClearGapM: toMetres(display, region),
                    },
                  });
              }}
            />
            {!designer && (
              <Field.HelperText>
                Edge-to-edge along the roof between tilted rows. The larger of
                this value and Row gap applies. Repack to apply it; this does
                not prove the rows are unshaded.
              </Field.HelperText>
            )}
          </Field.Legacy>
        ) : (
          !designer && (
            <Field.Legacy>
              <Field.Label>Row spacing (× panel rise)</Field.Label>
              <NumberInput
                value={value.flatRoof.rowSpacingMultiplier}
                min={1}
                max={5}
                step={0.1}
                disabled={disabled || !advancedEnabled}
                onChange={(rowSpacingMultiplier) =>
                  patch({
                    flatRoof: {
                      ...value.flatRoof,
                      rowSpacingMultiplier:
                        rowSpacingMultiplier ??
                        DEFAULT_SOLAR_PLACEMENT_RULES.flatRoof
                          .rowSpacingMultiplier,
                    },
                  })
                }
              />
            </Field.Legacy>
          )
        )}
      </div>
    </Form>
  );
};

export default PlacementRulesFields;
